import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, UserPlus, Edit2, Trash2, Loader2, 
  Star, RefreshCw, History, ArrowRight, Upload, FileText, Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = ['QB', 'WR', 'RB', 'TE', 'DB', 'LB', 'DL', 'Flex'];

// Player Card Component with Stats
const PlayerCard = ({ player, onEdit, onDelete, canEdit, onViewStats }) => (
  <div 
    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
    data-testid={`player-${player.id}`}
  >
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-lg">
        {player.jersey_number || '?'}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{player.name}</span>
          {player.is_captain && (
            <Badge variant="outline" className="text-yellow-600 border-yellow-600">
              <Star className="w-3 h-3 mr-1 fill-yellow-500" />
              Captain
            </Badge>
          )}
          {player.seasons_played > 1 && (
            <Badge variant="secondary">
              {player.seasons_played} seasons
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">
          {player.position}
          {player.email && ` • ${player.email}`}
        </div>
        {/* Player Stats Summary */}
        {player.stats && (player.stats.touchdowns > 0 || player.stats.passing_yards > 0 || player.stats.rushing_yards > 0) && (
          <div className="flex gap-3 mt-2 text-xs">
            {player.stats.touchdowns > 0 && (
              <span className="text-green-600 font-medium">{player.stats.touchdowns} TD</span>
            )}
            {player.stats.passing_yards > 0 && (
              <span className="text-blue-600">{player.stats.passing_yards} pass yds</span>
            )}
            {player.stats.rushing_yards > 0 && (
              <span className="text-orange-600">{player.stats.rushing_yards} rush yds</span>
            )}
            {player.stats.receiving_yards > 0 && (
              <span className="text-purple-600">{player.stats.receiving_yards} rec yds</span>
            )}
            {player.stats.interceptions > 0 && (
              <span className="text-red-600">{player.stats.interceptions} INT</span>
            )}
          </div>
        )}
      </div>
    </div>
    
    {canEdit && (
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => onEdit(player)} title="Edit player">
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(player.id)} title="Remove player">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    )}
  </div>
);

// Add/Edit Player Dialog
const PlayerDialog = ({ open, onOpenChange, player, onSave, saving }) => {
  const [form, setForm] = useState({
    name: '',
    jersey_number: '',
    position: 'Flex',
    email: '',
    phone: '',
    is_captain: false,
    stats: {
      touchdowns: 0,
      interceptions: 0,
      passing_yards: 0,
      rushing_yards: 0,
      receiving_yards: 0
    }
  });

  useEffect(() => {
    if (player) {
      setForm({
        name: player.name || '',
        jersey_number: player.jersey_number || '',
        position: player.position || 'Flex',
        email: player.email || '',
        phone: player.phone || '',
        is_captain: player.is_captain || false,
        stats: {
          touchdowns: player.stats?.touchdowns || 0,
          interceptions: player.stats?.interceptions || 0,
          passing_yards: player.stats?.passing_yards || 0,
          rushing_yards: player.stats?.rushing_yards || 0,
          receiving_yards: player.stats?.receiving_yards || 0
        }
      });
    } else {
      setForm({
        name: '',
        jersey_number: '',
        position: 'Flex',
        email: '',
        phone: '',
        is_captain: false,
        stats: {
          touchdowns: 0,
          interceptions: 0,
          passing_yards: 0,
          rushing_yards: 0,
          receiving_yards: 0
        }
      });
    }
  }, [player, open]);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Player name is required');
      return;
    }
    onSave({
      ...form,
      jersey_number: form.jersey_number ? parseInt(form.jersey_number) : null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{player ? 'Edit Player' : 'Add Player'}</DialogTitle>
          <DialogDescription>
            {player ? 'Update player information' : 'Add a new player to the roster'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="Player name"
                data-testid="player-name-input"
              />
            </div>
            <div>
              <Label>Jersey Number</Label>
              <Input
                type="number"
                value={form.jersey_number}
                onChange={(e) => setForm({...form, jersey_number: e.target.value})}
                placeholder="#"
                min="0"
                max="99"
                data-testid="player-jersey-input"
              />
            </div>
          </div>
          
          <div>
            <Label>Position</Label>
            <Select value={form.position} onValueChange={(v) => setForm({...form, position: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {POSITIONS.map(pos => (
                  <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                placeholder="player@email.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({...form, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_captain"
              checked={form.is_captain}
              onChange={(e) => setForm({...form, is_captain: e.target.checked})}
              className="rounded"
            />
            <Label htmlFor="is_captain" className="cursor-pointer">Team Captain</Label>
          </div>

          {/* Player Statistics */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-semibold mb-3 block">Season Statistics</Label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Touchdowns</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.stats.touchdowns}
                  onChange={(e) => setForm({
                    ...form, 
                    stats: {...form.stats, touchdowns: parseInt(e.target.value) || 0}
                  })}
                  data-testid="player-td-input"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Interceptions</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.stats.interceptions}
                  onChange={(e) => setForm({
                    ...form, 
                    stats: {...form.stats, interceptions: parseInt(e.target.value) || 0}
                  })}
                  data-testid="player-int-input"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Pass Yards</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.stats.passing_yards}
                  onChange={(e) => setForm({
                    ...form, 
                    stats: {...form.stats, passing_yards: parseInt(e.target.value) || 0}
                  })}
                  data-testid="player-pass-yds-input"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Rush Yards</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.stats.rushing_yards}
                  onChange={(e) => setForm({
                    ...form, 
                    stats: {...form.stats, rushing_yards: parseInt(e.target.value) || 0}
                  })}
                  data-testid="player-rush-yds-input"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Rec Yards</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.stats.receiving_yards}
                  onChange={(e) => setForm({
                    ...form, 
                    stats: {...form.stats, receiving_yards: parseInt(e.target.value) || 0}
                  })}
                  data-testid="player-rec-yds-input"
                />
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-player-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {player ? 'Update' : 'Add'} Player
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Roster Rollover Dialog
const RolloverDialog = ({ open, onOpenChange, sourceTeamId, teams, token, onComplete }) => {
  const [targetTeamId, setTargetTeamId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRollover = async () => {
    if (!targetTeamId) {
      toast.error('Please select a target team');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/roster/rollover`, {
        source_team_id: sourceTeamId,
        target_team_id: targetTeamId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message);
      onComplete();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rollover roster');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Roster Rollover</DialogTitle>
          <DialogDescription>
            Copy players from this team to another team for a new season
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <Label>Target Team</Label>
          <Select value={targetTeamId} onValueChange={setTargetTeamId}>
            <SelectTrigger>
              <SelectValue placeholder="Select team to copy roster to..." />
            </SelectTrigger>
            <SelectContent>
              {teams
                .filter(t => t.id !== sourceTeamId)
                .map(team => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          
          <p className="text-sm text-muted-foreground mt-4">
            This will copy all players to the target team. Duplicate players (by name or email) will be skipped.
            Players will be marked as returning with their season count incremented.
          </p>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleRollover} disabled={loading || !targetTeamId}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
            Rollover Roster
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// CSV Import Dialog
const CSVImportDialog = ({ open, onOpenChange, teamId, token, onComplete }) => {
  const [csvData, setCsvData] = useState('');
  const [parsedPlayers, setParsedPlayers] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const sampleCSV = `Name,Jersey,Position,Email,Phone
John Smith,12,QB,john@email.com,555-0101
Jane Doe,24,WR,jane@email.com,555-0102
Mike Johnson,7,RB,,555-0103`;

  const downloadSampleCSV = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'roster_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvData(event.target.result);
      parseCSV(event.target.result);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      setParsedPlayers([]);
      return;
    }
    
    // Parse header
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const nameIdx = headers.findIndex(h => h.includes('name'));
    const jerseyIdx = headers.findIndex(h => h.includes('jersey') || h.includes('number'));
    const positionIdx = headers.findIndex(h => h.includes('position') || h.includes('pos'));
    const emailIdx = headers.findIndex(h => h.includes('email'));
    const phoneIdx = headers.findIndex(h => h.includes('phone'));
    
    const players = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const name = nameIdx >= 0 ? values[nameIdx] : '';
      if (!name) continue;
      
      players.push({
        name,
        jersey_number: jerseyIdx >= 0 && values[jerseyIdx] ? parseInt(values[jerseyIdx]) || null : null,
        position: positionIdx >= 0 ? values[positionIdx] || 'Flex' : 'Flex',
        email: emailIdx >= 0 ? values[emailIdx] || null : null,
        phone: phoneIdx >= 0 ? values[phoneIdx] || null : null,
        is_captain: false
      });
    }
    setParsedPlayers(players);
  };

  const handleImport = async () => {
    if (parsedPlayers.length === 0) {
      toast.error('No players to import');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    try {
      const res = await axios.post(`${API}/teams/${teamId}/players/bulk`, {
        players: parsedPlayers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setImportResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} players`);
        onComplete();
      }
      if (res.data.skipped > 0) {
        toast.warning(`Skipped ${res.data.skipped} players (duplicates or errors)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const resetDialog = () => {
    setCsvData('');
    setParsedPlayers([]);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetDialog(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Bulk Import Players
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file or paste player data to quickly add multiple players
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* File Upload */}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="gap-2"
              data-testid="csv-upload-btn"
            >
              <FileText className="h-4 w-4" />
              Upload CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={downloadSampleCSV}
              className="gap-1 text-muted-foreground"
            >
              <Download className="h-3 w-3" />
              Download Template
            </Button>
          </div>
          
          {/* CSV Text Area */}
          <div>
            <Label>Or paste CSV data directly</Label>
            <Textarea
              value={csvData}
              onChange={(e) => {
                setCsvData(e.target.value);
                parseCSV(e.target.value);
              }}
              placeholder={sampleCSV}
              rows={6}
              className="font-mono text-sm mt-2"
              data-testid="csv-textarea"
            />
          </div>
          
          {/* Preview */}
          {parsedPlayers.length > 0 && (
            <div>
              <Label className="mb-2 block">Preview ({parsedPlayers.length} players)</Label>
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">#</th>
                      <th className="text-left p-2">Position</th>
                      <th className="text-left p-2">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedPlayers.slice(0, 10).map((p, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{p.name}</td>
                        <td className="p-2">{p.jersey_number || '-'}</td>
                        <td className="p-2">{p.position}</td>
                        <td className="p-2 text-muted-foreground">{p.email || '-'}</td>
                      </tr>
                    ))}
                    {parsedPlayers.length > 10 && (
                      <tr className="border-t">
                        <td colSpan={4} className="p-2 text-center text-muted-foreground">
                          ... and {parsedPlayers.length - 10} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Import Result */}
          {importResult && (
            <div className={`p-4 rounded-lg ${importResult.imported > 0 ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
              <p className="font-medium">
                Imported: {importResult.imported} | Skipped: {importResult.skipped}
              </p>
              {importResult.errors.length > 0 && (
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleImport} 
            disabled={importing || parsedPlayers.length === 0}
            className="gap-2"
            data-testid="import-players-btn"
          >
            {importing && <Loader2 className="h-4 w-4 animate-spin" />}
            Import {parsedPlayers.length} Players
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Roster Management Page
export const RosterManagement = () => {
  const { teamId } = useParams();
  const { token, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [rolloverDialogOpen, setRolloverDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  
  // Roster history
  const [rosterHistory, setRosterHistory] = useState(null);

  const canEdit = user?.role === 'admin' || user?.role === 'organizer';

  useEffect(() => {
    fetchData();
  }, [teamId]);

  const fetchData = async () => {
    try {
      const [teamRes, playersRes, teamsRes, historyRes] = await Promise.all([
        axios.get(`${API}/teams/${teamId}`),
        axios.get(`${API}/teams/${teamId}/players`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/teams/${teamId}/roster-history`)
      ]);
      
      setTeam(teamRes.data);
      setPlayers(playersRes.data);
      setAllTeams(teamsRes.data);
      setRosterHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching roster data:', error);
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = () => {
    setEditingPlayer(null);
    setPlayerDialogOpen(true);
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setPlayerDialogOpen(true);
  };

  const handleSavePlayer = async (playerData) => {
    setSaving(true);
    try {
      if (editingPlayer) {
        await axios.put(`${API}/players/${editingPlayer.id}`, playerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Player updated');
      } else {
        await axios.post(`${API}/teams/${teamId}/players`, playerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Player added');
      }
      setPlayerDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save player');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Remove this player from the roster?')) return;
    
    try {
      await axios.delete(`${API}/players/${playerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Player removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove player');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const returningCount = rosterHistory?.returning_players || 0;
  const newCount = rosterHistory?.new_players || 0;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          {team?.logo_url && (
            <img src={team.logo_url} alt={team.name} className="w-16 h-16 rounded-lg object-cover" />
          )}
          <div>
            <h1 className="text-3xl font-bold">{team?.name}</h1>
            <p className="text-muted-foreground">
              {team?.age_category} • {team?.gender} • Roster Management
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => setCsvImportOpen(true)} data-testid="bulk-import-btn">
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </Button>
              <Button variant="outline" onClick={() => setRolloverDialogOpen(true)}>
                <History className="mr-2 h-4 w-4" />
                Roster Rollover
              </Button>
              <Button onClick={handleAddPlayer} data-testid="add-player-btn">
                <UserPlus className="mr-2 h-4 w-4" />
                Add Player
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Roster Summary */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Players</CardDescription>
            <CardTitle className="text-3xl">{players.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Returning Players</CardDescription>
            <CardTitle className="text-3xl text-green-600">{returningCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>New Players</CardDescription>
            <CardTitle className="text-3xl text-blue-600">{newCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Player List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Roster
          </CardTitle>
          <CardDescription>
            Manage your team players, positions, and contact information
          </CardDescription>
        </CardHeader>
        <CardContent>
          {players.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">No Players Yet</h3>
              <p className="text-muted-foreground mb-4">
                Add players to build your team roster
              </p>
              {canEdit && (
                <Button onClick={handleAddPlayer}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add First Player
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {players.map(player => (
                <PlayerCard
                  key={player.id}
                  player={player}
                  onEdit={handleEditPlayer}
                  onDelete={handleDeletePlayer}
                  canEdit={canEdit}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Position Breakdown */}
      {players.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Position Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {POSITIONS.map(pos => {
                const count = players.filter(p => p.position === pos).length;
                if (count === 0) return null;
                return (
                  <Badge key={pos} variant="secondary" className="text-sm py-1 px-3">
                    {pos}: {count}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <PlayerDialog
        open={playerDialogOpen}
        onOpenChange={setPlayerDialogOpen}
        player={editingPlayer}
        onSave={handleSavePlayer}
        saving={saving}
      />
      
      <RolloverDialog
        open={rolloverDialogOpen}
        onOpenChange={setRolloverDialogOpen}
        sourceTeamId={teamId}
        teams={allTeams}
        token={token}
        onComplete={fetchData}
      />
      
      <CSVImportDialog
        open={csvImportOpen}
        onOpenChange={setCsvImportOpen}
        teamId={teamId}
        token={token}
        onComplete={fetchData}
      />
    </div>
  );
};
